import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown, Info, Check } from 'lucide-react'
import { useEnergyStore } from '@/stores/useEnergyStore'

interface SelectProps {
  label: string
  value: string
  options: string[] | { label: string; value: string }[]
  onChange: (value: string) => void
  darkMode?: boolean
  helpKey?: string
}

export const Select: React.FC<SelectProps> = ({
  label,
  value,
  options,
  onChange,
  darkMode = true,
  helpKey,
}) => {
  const openHelpToItem = useEnergyStore((s) => s.openHelpToItem)
  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedOption = options.find((o) => (typeof o === 'string' ? o : o.value) === value)
  const displayLabel = selectedOption 
    ? (typeof selectedOption === 'string' ? selectedOption : selectedOption.label)
    : ''

  return (
    <div className="space-y-2" ref={selectRef}>
      {label && (
        <div className="flex items-center gap-1">
          {helpKey && (
            <button
              onClick={() => openHelpToItem(helpKey)}
              className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
              }`}
              title={`Help with ${label}`}
            >
              <Info size={10} />
            </button>
          )}
          <label
            className={`text-[10px] font-black uppercase tracking-wider leading-tight ${
              darkMode ? 'text-slate-500' : 'text-slate-400'
            }`}
          >
            {label}
          </label>
        </div>
      )}
      <div className="relative group">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full flex items-center justify-between gap-3 rounded-xl px-4 py-3 sm:py-2.5 text-sm font-bold transition-all duration-200 outline-none border cursor-pointer text-left ${
            darkMode
              ? isOpen 
                ? 'bg-white/10 border-emerald-500/50 text-white shadow-lg shadow-black/20' 
                : 'bg-white/5 border-white/5 text-slate-200 hover:bg-white/10 hover:text-white'
              : isOpen
                ? 'bg-white border-emerald-500/50 text-slate-900 shadow-md shadow-emerald-500/10'
                : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-white hover:border-slate-300'
          }`}
        >
          <span className="flex-1 leading-tight break-words">{displayLabel}</span>
          <ChevronDown
            className={`w-4 h-4 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-emerald-500' : darkMode ? 'text-slate-500' : 'text-slate-400'}`}
          />
        </button>

        {isOpen && (
          <div 
            className={`absolute z-50 w-full mt-2 py-2 rounded-xl border shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top ${
              darkMode 
                ? 'bg-slate-800 border-white/10 shadow-black/50' 
                : 'bg-white border-slate-200 shadow-slate-200/50'
            }`}
          >
            <div className="max-h-[50vh] overflow-y-auto custom-scrollbar px-2 space-y-1">
              {options.map((option) => {
                const optValue = typeof option === 'string' ? option : option.value
                const optLabel = typeof option === 'string' ? option : option.label
                const isSelected = optValue === value
                
                return (
                  <button
                    key={optValue}
                    type="button"
                    onClick={() => {
                      onChange(optValue)
                      setIsOpen(false)
                    }}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-3 sm:py-2 rounded-lg text-sm font-bold transition-all cursor-pointer text-left ${
                      isSelected
                        ? darkMode 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-emerald-50 text-emerald-700'
                        : darkMode
                          ? 'text-slate-400 hover:bg-white/10 hover:text-white'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <span className="flex-1 leading-tight break-words">{optLabel}</span>
                    {isSelected && <Check size={16} className={`shrink-0 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
